/**
 * Icon point-cloud loader — ICONS_INTEGRATION §2.
 *
 * Icons ship as JSON point clouds in `client/public/icons/`, fetched lazily on
 * first incarnation of a tactic and cached. They are COMMITTED, unlike AlvaAR:
 * they are the artwork, not a GPL dependency.
 *
 * The parsed JS arrays are converted to typed arrays immediately and the
 * originals dropped — 10,000 nested arrays per icon is needless GC pressure, and
 * the Float32Arrays are directly uploadable as WebGL buffers.
 *
 * Coordinates are normalised to 0..1 at load so nothing downstream needs to know
 * the source resolution. Multiply x by `aspect` when placing in world space or
 * the icon is squashed.
 */

import type { Tactic } from '@wintermute/orchestrator';

export interface IconCloud {
  id: string;
  /** Normalised 0..1, xy interleaved: [x0,y0, x1,y1, ...]. y is DOWN. */
  pos: Float32Array;
  /** 0..1 sRGB, rgb interleaved: [r0,g0,b0, ...]. */
  col: Float32Array;
  count: number;
  /** width / height of the source, for correct non-square placement. */
  aspect: number;
  /** Minimum point spacing in NORMALISED units — drives dot radius. */
  spacing: number;
}

export interface IconMeta {
  id: string;
  file: string;
  abbrev: string;
  full: string;
  tactic: Tactic | null;
  rank: 'local' | 'deesis' | 'forefathers';
  patronage?: string;
  patronOf?: string;
  placement?: string;
}

interface RawIcon {
  format?: string;
  version?: number;
  source?: { width: number; height: number };
  sampling?: { minimum_distance_pixels?: number };
  point_schema?: string[];
  points: number[][];
}

interface RawManifest {
  icons: IconMeta[];
  notYetDelivered?: { id: string; abbrev: string; tactic: Tactic | null; reason: string }[];
}

/**
 * Which cloud each tactic incarnates as.
 *
 * Three of five tactics have delivered artwork. The other two are procedural by
 * design and need no file (ICONS_INTEGRATION §5):
 *
 * - `doubt` → **the Twin**: any cloud drawn twice, misregistered and diverging.
 *   It borrows `already-decided` purely as a source of points; the effect is the
 *   doubling, not the drawing.
 * - `identity` → **the Forefather**: assembled from live SLAM feature points, so
 *   it has no cloud at all. `iconIdForTactic` returns null and the renderer
 *   sources points from the tracker.
 */
const TACTIC_ICON: Record<Tactic, string | null> = {
  nostalgia: 'she-who-remembers',
  fear: 'watchman',
  authority: 'already-decided',
  doubt: 'already-decided', // borrowed; rendered as the Twin
  identity: null, // the Forefather — live tracking points only
};

/** True when this tactic's figure is built from live tracking points. */
export function isProcedural(tactic: Tactic): boolean {
  return TACTIC_ICON[tactic] === null;
}

/** True when this tactic renders as two misregistered copies. */
export function isTwin(tactic: Tactic): boolean {
  return tactic === 'doubt';
}

export function iconIdForTactic(tactic: Tactic): string | null {
  return TACTIC_ICON[tactic] ?? null;
}

const cache = new Map<string, Promise<IconCloud>>();
let manifestPromise: Promise<RawManifest> | null = null;

export async function loadManifest(): Promise<RawManifest> {
  manifestPromise ??= fetch('/icons/manifest.json', { cache: 'force-cache' })
    .then((r) => {
      if (!r.ok) throw new Error(`icon manifest ${r.status}`);
      return r.json() as Promise<RawManifest>;
    })
    .catch((e) => {
      manifestPromise = null; // let a later attempt retry
      throw e;
    });
  return manifestPromise;
}

/** Cast metadata for an icon id, or null if unknown. */
export async function iconMeta(id: string): Promise<IconMeta | null> {
  const m = await loadManifest();
  return m.icons.find((i) => i.id === id) ?? null;
}

/**
 * Fetch and decode one icon. Cached by id — repeated calls share one request.
 * Rejects (rather than returning a broken cloud) so callers can degrade to the
 * text card, which is the correct fallback.
 */
export function loadIcon(id: string): Promise<IconCloud> {
  const hit = cache.get(id);
  if (hit) return hit;

  const p = (async () => {
    const res = await fetch(`/icons/${id}.json`, { cache: 'force-cache' });
    if (!res.ok) throw new Error(`icon ${id}: HTTP ${res.status}`);
    const d = (await res.json()) as RawIcon;
    return decodeIcon(id, d);
  })().catch((e) => {
    cache.delete(id); // a transient failure must not poison the cache
    throw e;
  });

  cache.set(id, p);
  return p;
}

/** Split out from loadIcon so it can be tested without a network. */
export function decodeIcon(id: string, d: RawIcon): IconCloud {
  const W = d.source?.width ?? 0;
  const H = d.source?.height ?? 0;
  if (!(W > 0 && H > 0)) throw new Error(`icon ${id}: missing source dimensions`);

  const pts = d.points;
  if (!Array.isArray(pts) || pts.length === 0) throw new Error(`icon ${id}: no points`);

  const n = pts.length;
  const pos = new Float32Array(n * 2);
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const p = pts[i]!;
    // Schema is [x, y, z, r, g, b]; z is always 0 — the icons are flat by design.
    pos[i * 2] = p[0]! / W;
    pos[i * 2 + 1] = p[1]! / H; // y DOWN, matching the source
    col[i * 3] = p[3]! / 255;
    col[i * 3 + 1] = p[4]! / 255;
    col[i * 3 + 2] = p[5]! / 255;
  }

  return {
    id,
    pos,
    col,
    count: n,
    aspect: W / H,
    // Normalised against width so it composes with the x-by-aspect placement.
    spacing: (d.sampling?.minimum_distance_pixels ?? 7) / W,
  };
}

/** Test seam. */
export function __clearIconCache(): void {
  cache.clear();
  manifestPromise = null;
}
